import isEmpty from 'lodash-es/isEmpty';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../components/Button';
import { Flex } from '../../components/Flex';
import { InfoNotice } from '../../components/InfoNotice';
import { Section } from '../../components/Section';
import { SpinnerFullScreen } from '../../components/SpinnerFullScreen';
import { Text } from '../../components/Text';
import { AtiraLinkPlan } from '../../model/plan/Plan';
import { AtiraLinkPlanType } from '../../model/plan/types/AtiraLinkPlanType.enum';
import { paymentActions } from '../../redux/payment/payment.slice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { userSliceSelectors } from '../../redux/user/user.selector';
import { AtiraToast } from '../../utils/AtiraToast';
import { standardDate } from '../../utils/Date';
import { isUserOnUnpaidPlan } from '../../utils/Payment';
import { getPlanTitle, toUSD } from '../../utils/pricingUtils';

const SpanBold = styled.span`
  font-weight: bold;
`;

export const Subscription: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const plan = user?.plan || ({} as AtiraLinkPlan);
  const isOnFreeTrial =
    plan.freeTrial?.hasUsed && (plan.freeTrial?.endsAt?.length || 0) > 0;
  const isOnUnpaidPlan = isUserOnUnpaidPlan(user);

  const onUpdatePaymentMethod = async () => {
    try {
      setLoading(true);
      const url = await dispatch(
        paymentActions.getUpdatePaymentMethodURL(),
      ).unwrap();
      window.createLemonSqueezy();
      window.LemonSqueezy.Url.Open(url);
    } catch (e: any) {
      console.error('Failed to update payment method:', e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  if (isEmpty(plan)) {
    return <SpinnerFullScreen />;
  }

  return (
    <Flex flexDirection="column" gap="m" flexGrow={1}>
      <Section title={t('subscription.plan.title')}>
        <Text color="fgSecondary">{t('subscription.plan.description')}</Text>

        <Flex flexDirection="column" gap="s">
          <Text>
            <SpanBold>{t('subscription.plan.title')}: </SpanBold>
            {t(getPlanTitle(plan.type))}
          </Text>

          <Text>
            <Trans
              i18nKey={'subscription.status'}
              values={{
                status: t(
                  `subscription.status.${isOnFreeTrial ? 'freeTrial' : plan.lemonSubscriptionData?.subscription.status || 'not_subscribed'}`,
                ),
              }}
              components={[<SpanBold />]}
            />
          </Text>

          {plan.lemonSubscriptionData?.subscription || isOnFreeTrial ? (
            <InfoNotice
              info={
                <Trans
                  i18nKey={`subscription.status.${isOnFreeTrial ? 'freeTrial' : plan.lemonSubscriptionData?.subscription.status}.description`}
                  values={{
                    amount: toUSD(
                      Number(plan.lemonSubscriptionData?.variant?.price!),
                    ),
                    date: standardDate(plan.lemonPlanData?.renewsAt!),
                    last4:
                      plan.lemonSubscriptionData?.subscription.card_last_four,
                  }}
                  components={[<SpanBold />]}
                />
              }
            />
          ) : null}
        </Flex>
      </Section>

      {!isOnUnpaidPlan && plan.type !== AtiraLinkPlanType.PLAN_0 ? (
        <Section title={t('subscription.page.payment.title')}>
          <Text color="fgSecondary">
            {t('subscription.page.payment.description')}
          </Text>

          <Button
            loading={loading}
            height="2.5rem"
            width="17rem"
            onClick={onUpdatePaymentMethod}
          >
            {t('subscription.page.payment.button')}
          </Button>
        </Section>
      ) : null}
    </Flex>
  );
};
